export default {
  data() {
    return {
      internalSelected: [...this.modelValue]
    }
  },
  computed: {
    selectedElements: {
      get() {
        return this.internalSelected
      },
      set(value) {
        this.internalSelected = value
        this.$emit('update:modelValue', value)
      }
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        const same = newVal.length === this.internalSelected.length &&
          newVal.every(id => this.internalSelected.includes(id))
        if (!same) {
          this.internalSelected = [...newVal]
          this.syncMinimapState()
        }
      }
    },
    initialCategory() {
      this.applyAvailability()
    },
    unavailableIds: {
      handler() {
        this.applyAvailability()
      },
      deep: true
    },
    maxCount(newVal) {
      if (newVal !== null && this.selectedElements.length > newVal) {
        const excessCount = this.selectedElements.length - newVal
        const removed = this.selectedElements.slice(-excessCount) // die zuletzt hinzugefügten zuerst entfernen

        removed.forEach(id => {
          const el = document.getElementById(id)
          this.selectedElements = this.selectedElements.filter(x => x !== id)
          if (el) {
            el.removeAttribute('stroke')
            el.removeAttribute('stroke-width')
          }
          this.mirrorSelectionState(id)
        })

        this.showError(`Die maximale Anzahl wurde auf ${newVal} reduziert, überzählige Plätze wurden entfernt.`)
      }
    }
  },
  methods: {
    handleTap(clientX, clientY) {
      const target = document.elementFromPoint(clientX, clientY)
      if (!target) return
      this.onSvgClick({ target })
    },
    
    // ---- Kombinierte Verfügbarkeits-Logik: Kategorie + explizite unavailableIds ----
    applyAvailability() {
      if (!this.svgRoot) return

      const categoryNode = this.initialCategory ? document.getElementById(this.initialCategory) : null
      if (this.initialCategory && !categoryNode) {
        this.showError(`Kategorie "${this.initialCategory}" wurde nicht gefunden.`)
      }

      const stuffNode = document.getElementById('stuff')
      const explicitUnavailable = new Set(this.unavailableIds || [])
      const allPaths = Array.from(this.svgRoot.querySelectorAll('path[id]'))

      const idsToDeselect = []

      allPaths.forEach(path => {
        if (stuffNode && stuffNode.contains(path)) return

        const id = path.getAttribute('id')

        const outsideCategory = categoryNode ? !categoryNode.contains(path) : false
        const shouldBeUnavailable = outsideCategory || explicitUnavailable.has(id)

        if (shouldBeUnavailable) {
          path.classList.add('unavailable')
          if (this.selectedElements.includes(id)) {
            idsToDeselect.push(id)
          }
        } else {
          path.classList.remove('unavailable')
        }

        this.mirrorUnavailableState(id)
      })

      // Bereits ausgewählte Plätze, die jetzt gesperrt wurden, müssen aus der Auswahl entfernt werden
      if (idsToDeselect.length > 0) {
        this.selectedElements = this.selectedElements.filter(id => !idsToDeselect.includes(id))
        idsToDeselect.forEach(id => {
          const el = document.getElementById(id)
          if (el) {
            el.removeAttribute('stroke')
            el.removeAttribute('stroke-width')
          }
          this.mirrorSelectionState(id)
        })
        this.showError('Ein oder mehrere ausgewählte Plätze wurden zwischenzeitlich gesperrt und aus deiner Auswahl entfernt.')
      }
    },
    onSvgClick(e) {
      const target = e.target
      if (!this.svgRoot || target === this.svgRoot) return
      if (!this.svgRoot.contains(target)) return

      if (target.tagName.toLowerCase() !== 'path') return
      const id = target.getAttribute('id')
      if (!id) return

      const stuffNode = document.getElementById('stuff')
      if (stuffNode && stuffNode.contains(target)) return

      if (this.ruleBlockUnavailable && target.classList.contains('unavailable')) {
        this.showError('Dieser Platz ist nicht verfügbar.')
        return
      }

      const path = this.buildElementPath(target, this.svgRoot)
      this.handleElementClick(path, target)
    },
    buildElementPath(target, svgRoot) {
      const path = []
      let el = target
      while (el && el !== svgRoot) {
        const id = el.getAttribute && el.getAttribute('id')
        if (id) path.unshift({ id, tag: el.tagName })
        el = el.parentElement
      }
      return path
    },
    canHandleElementClick(element, options = {}) {
      const { allowNewIsland = false } = options
      const id = element.getAttribute('id')
      if (!id) return false

      if (this.ruleBlockUnavailable && this.isUnavailable(id)) {
        this.showError('Dieser Platz ist nicht verfügbar.')
        return false
      }

      const isCurrentlySelected = this.selectedElements.includes(id)
      
      if (isCurrentlySelected) {
        const remaining = this.selectedElements.filter(x => x !== id)
        if (remaining.length === 0) return true

        if (this.ruleMustStayConnected) {
          const islandsBefore = this.getIslands(this.selectedElements).length
          const islandsAfter = this.getIslands(remaining).length

          // Abwählen darf eine Gruppe nie in mehrere Teile trennen
          if (islandsAfter > islandsBefore) {
            this.showError('Dieser Platz kann nicht abgewählt werden, da sonst deine Gruppe getrennt würde. Nimm stattdessen einen Platz am Rand weg.')
            return false
          }
        }

        if (!this.isValidSelectionState(remaining)) {
          this.showError('Dieser Platz kann nicht abgewählt werden, da sonst eine ungültige Auswahl entstehen würde.')
          return false
        }
        return true
      }

      if (this.maxCount !== null && this.selectedElements.length >= this.maxCount) {
        this.showError(`Es können maximal ${this.maxCount} Plätze ausgewählt werden.`)
        return false
      }

      if (this.selectedElements.length === 0) {
        if (!this.canStartSelection(id)) {
          this.showError('Die Auswahl muss am Rand der Tafel oder neben einem gesperrten Platz beginnen.')
          return false
        }
        return true
      }

      const grenztAn = this.selectedElements.some(selectedId => this.areAdjacentById(selectedId, id))

      if (!grenztAn) {
        // Neue, unverbundene Insel nur erlauben, wenn an der bestehenden Auswahl
        // tatsächlich kein freier Nachbarplatz mehr existiert (Tisch/Tafel voll)
        if (!allowNewIsland && this.ruleMustStayConnected && this.hasReachableAvailableSeat()) {
          this.showError('Bitte wähle zuerst die freien Plätze direkt neben deiner aktuellen Auswahl, bevor du an anderer Stelle beginnst.')
          return false
        }
        if (!this.canStartSelection(id)) {
          this.showError('Dieser Platz ist kein gültiger neuer Startpunkt (Tafel-Rand oder neben einem gesperrten Platz).')
          return false
        }
      }

      if (!this.isValidSelectionState([...this.selectedElements, id])) {
        this.showError('Diese Auswahl würde eine ungültige Fläche ergeben.')
        return false
      }

      return true
    },
    handleElementClick(path, element) {
      // Manueller Klick: allowNewIsland bleibt false -> hasReachableAvailableSeat greift
      if (!this.canHandleElementClick(element)) return

      const id = element.getAttribute('id')
      if (!id) return

      if (this.selectedElements.indexOf(id) === -1) {
        this.selectedElements = [...this.selectedElements, id]
        element.setAttribute('stroke', this.selectionColor)
        element.setAttribute('stroke-width', '2')
      } else {
        this.selectedElements = this.selectedElements.filter(x => x !== id)
        element.removeAttribute('stroke')
        element.removeAttribute('stroke-width')
      }

      this.mirrorSelectionState(id)
    },
    selectElementById(id, { allowNewIsland = true } = {}) {
      // Programmatischer Aufruf (z.B. aus selectBestGroup): Insel-Guard standardmäßig übersprungen,
      // da hier das Programm selbst bereits entschieden hat, wo welche Insel sinnvoll ist
      const element = document.getElementById(id)
      if (!element) return false
      if (!this.canHandleElementClick(element, { allowNewIsland })) return false

      if (this.selectedElements.indexOf(id) === -1) {
        this.selectedElements = [...this.selectedElements, id]
        element.setAttribute('stroke', this.selectionColor)
        element.setAttribute('stroke-width', '2')
      }

      this.mirrorSelectionState(id)
      return true
    },
    clearSelection() {
      const ids = [...this.selectedElements]
      this.selectedElements = []

      ids.forEach(id => {
        const el = document.getElementById(id)
        if (el) {
          el.removeAttribute('stroke')
          el.removeAttribute('stroke-width')
        }
        this.mirrorSelectionState(id)
      })
    },
    deselectElementById(id) {
      const element = document.getElementById(id)
      if (!element) return false

      if (!this.selectedElements.includes(id)) {
        this.showError('Dieser Platz ist aktuell nicht ausgewählt.')
        return false
      }

      if (!this.canHandleElementClick(element)) {
        // canHandleElementClick hat bereits eine passende Fehlermeldung gezeigt
        // (z.B. "Dieser Platz kann nicht abgewählt werden, da sonst die Auswahl getrennt würde.")
        return false
      }

      this.selectedElements = this.selectedElements.filter(x => x !== id)
      element.removeAttribute('stroke')
      element.removeAttribute('stroke-width')

      this.mirrorSelectionState(id)
      return true
    },
    setUnavailableIds(ids) {
      if (!this.svgRoot) return

      const idSet = new Set(ids)
      const allPaths = Array.from(this.svgRoot.querySelectorAll('path[id]'))

      allPaths.forEach(path => {
        const id = path.getAttribute('id')
        if (idSet.has(id)) {
          path.classList.add('unavailable')
        }
        this.mirrorUnavailableState(id)
      })
    },
    restrictToCategory(categoryId) {
      if (!this.svgRoot) return

      const categoryNode = document.getElementById(categoryId)
      if (!categoryNode) {
        this.showError(`Kategorie "${categoryId}" wurde nicht gefunden.`)
        return
      }

      const stuffNode = document.getElementById('stuff')
      const allPaths = Array.from(this.svgRoot.querySelectorAll('path[id]'))

      allPaths.forEach(path => {
        if (stuffNode && stuffNode.contains(path)) return

        const id = path.getAttribute('id')

        if (categoryNode.contains(path)) {
          path.classList.remove('unavailable')
        } else {
          path.classList.add('unavailable')
        }

        this.mirrorUnavailableState(id)
      })
    }
  }
}
